import { useEffect, useState } from 'react'
import type { AutomationSetting } from '@shared/types'
import { DEFAULT_AUTOMATION } from '@shared/types'
import { useStore } from '../../store'
import { api } from '../../lib/api'
import { cn, relativeTime } from '../../lib/utils'
import { Switch } from './TradingRiskSettings'

type Wake = Awaited<ReturnType<typeof api.wakes>>['wakes'][number]
const SOURCE: Record<string, string> = { task: 'Task', worker: 'Worker', watch: 'Watcher', scan: 'Scan' }
const DOT: Record<string, string> = { ok: 'bg-up', quiet: 'bg-[var(--color-fg-faint)]', running: 'bg-[var(--color-fg-muted)] animate-pulse', skipped: 'bg-[var(--color-fg-subtle)]', error: 'bg-down' }

/**
 * Automations: the daily budget of AI runs no one typed (scheduled tasks, watchers, scans, worker
 * callbacks), whether the AI may schedule its own follow-ups, and the log of those runs.
 */
export function AutomationSettings() {
  const settings = useStore((s) => s.settings)
  const update = useStore((s) => s.updateSettings)
  const selectChat = useStore((s) => s.selectChat)
  const a: AutomationSetting = settings?.automation ?? DEFAULT_AUTOMATION
  const [budget, setBudget] = useState(String(a.wakeBudget))
  const [perChat, setPerChat] = useState(String(a.aiPerChat))
  const [log, setLog] = useState<{ budget: { used: number; max: number; unlimited?: boolean }; wakes: Wake[] } | null>(null)
  useEffect(() => {
    setBudget(String(a.wakeBudget))
    setPerChat(String(a.aiPerChat))
  }, [a.wakeBudget, a.aiPerChat])
  useEffect(() => {
    api.wakes().then(setLog).catch(() => {})
  }, [a.wakeBudget])

  const save = (patch: Partial<AutomationSetting>) => void update({ automation: { ...a, ...patch } })
  const num = 'num w-20 rounded-lg border border-[var(--line-soft)] bg-[var(--color-inset)] px-2.5 py-1.5 text-[13px] text-fg focus:outline-none'

  return (
    <section id="settings-automations" className="card p-4">
      <h2 className="text-[14px] font-semibold text-fg">Automations</h2>
      <p className="mt-0.5 text-[12.5px] text-fg-subtle">
        AI runs no one typed — scheduled tasks, watchers, market scans and worker callbacks. Each one is a full AI answer: on the paid API key they share a daily budget, on your own Grok subscription they are unlimited.
      </p>
      <div className="mt-3 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[13px] text-fg">Daily AI-wakeup budget</div>
            <div className="text-[11.5px] text-fg-subtle">
              {log?.budget.unlimited
                ? 'Not in force: your Grok subscription pays, so wakeups are unlimited. It applies only when the API key has to answer'
                : log
                  ? `${log.budget.used} used today (US Eastern day).`
                  : ' '}
              {log?.budget.unlimited ? ` (${log.budget.used} today).` : ' Past it, wakeups are skipped and you get one Telegram notice.'}
            </div>
          </div>
          <input
            type="number"
            min={0}
            max={1000}
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            onBlur={() => {
              const n = Math.min(1000, Math.max(0, Math.round(Number(budget) || 0)))
              setBudget(String(n))
              if (n !== a.wakeBudget) save({ wakeBudget: n })
            }}
            className={num}
          />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[13px] text-fg">The AI may schedule its own follow-ups</div>
            <div className="text-[11.5px] text-fg-subtle">When a wakeup finds something, it can plan the next check in the same chat (always listed there, cancellable).</div>
          </div>
          <Switch on={a.aiSchedule} onChange={(v) => save({ aiSchedule: v })} label="AI follow-ups" />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[13px] text-fg">AI-made schedules per chat</div>
            <div className="text-[11.5px] text-fg-subtle">At most this many active at once in one chat.</div>
          </div>
          <input
            type="number"
            min={0}
            max={50}
            value={perChat}
            onChange={(e) => setPerChat(e.target.value)}
            onBlur={() => {
              const n = Math.min(50, Math.max(0, Math.round(Number(perChat) || 0)))
              setPerChat(String(n))
              if (n !== a.aiPerChat) save({ aiPerChat: n })
            }}
            className={num}
          />
        </div>
      </div>

      <div className="mt-4">
        <div className="mb-1.5 text-[11px] uppercase tracking-wide text-fg-subtle">Recent wakeups</div>
        {!log ? (
          <div className="text-[12px] text-fg-faint">Loading…</div>
        ) : !log.wakes.length ? (
          <div className="text-[12px] text-fg-faint">None yet.</div>
        ) : (
          <div className="max-h-72 space-y-0.5 overflow-y-auto">
            {log.wakes.map((w) => (
              <button
                key={w.id}
                disabled={!w.chatId}
                onClick={() => w.chatId && void selectChat(w.chatId)}
                className="flex w-full items-start gap-2 rounded-md px-1.5 py-1 text-left hover:bg-[var(--color-surface-2)] disabled:cursor-default"
              >
                <span className={cn('mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full', DOT[w.status] ?? 'bg-[var(--color-fg-subtle)]')} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-2 text-[12px] text-fg-muted">
                    <span className="truncate">
                      <span className="text-fg-faint">{SOURCE[w.source] ?? w.source} · </span>
                      {w.title}
                    </span>
                    <span className="num shrink-0 text-[11px] text-fg-faint">{relativeTime(w.at)}</span>
                  </span>
                  {w.detail && <span className="block truncate text-[11px] text-fg-subtle">{w.detail}</span>}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
